import { useRef, useState } from 'react'
import styled from 'styled-components'
import { signOut } from 'firebase/auth'
import { auth } from '~/utils/client/auth'
import { Layer } from '../../../packages/general/variable/spaceTime.types'
import { VariableWithResult } from '../../../packages/general/variable/variables.typing'
import recommendedLayerForVariable from '../../../packages/general/variable/recommendedLayerForVariable.util'
import useAPI from '~/prix/react/hooks/api'
import { useNavigate } from 'react-router'
import { useClickAway } from 'react-use'
import { faBars, faXmark } from '@fortawesome/free-solid-svg-icons'
import AvatarHeader from '~/components/avatarHeader'
import MenuItem from '~/components/menuItem'
import { ButtonVariants } from '~/components/buttonVariants'
import {
  MapMenuProps,
  mapTypes,
  mapTypesLabels,
  map3DOptions,
  map3DOptionsLabels,
} from './mapMenu.types'

const Wrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.padding(1)};
  right: ${({ theme }) => theme.padding(1)};
`

const WrapperButtons = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const Button = styled(ButtonVariants)`
  width: 40px;
  height: 40px;
  transition: box-shadow 0.2s ease-in-out;
  box-shadow: ${({ theme }) => theme.shadow(1)};
  border-radius: 0;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadow(2)};
  }

  /* First button */
  &:first-child {
    border-top-left-radius: ${({ theme }) => theme.borderRadius};
    border-top-right-radius: ${({ theme }) => theme.borderRadius};
  }

  /* Last button */
  &:last-child {
    border-bottom-left-radius: ${({ theme }) => theme.borderRadius};
    border-bottom-right-radius: ${({ theme }) => theme.borderRadius};
  }

  &:not(:last-child) {
    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  }
`

const NavigationMenu = styled.nav<{ $isOpen: boolean }>`
  z-index: 2;
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.padding(1)});
  right: 0;
  width: 284px;
  background-color: white;
  font-size: 1rem;
  font-weight: normal;
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.borderRadius};
  color: ${({ theme }) => theme.colors.primary};
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '-30%')});
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out, visibility 0.3s ease-in-out;
  box-shadow: ${({ theme }) => theme.shadow(1)};
  gap: ${({ theme }) => theme.padding(1)};
  padding: ${({ theme }) => theme.padding(2)};
  color: #414141;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    max-height: 90vh;
    font-size: 0.8rem;
  }

  hr {
    margin: 0.5rem 0;
    border: none;
    border-top: 2px solid rgba(0, 0, 0, 0.1);
  }

  .legends-wrapper {
    display: none;
    flex-direction: column;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      display: flex;
      overflow-y: auto;
    }

    .legends {
      display: flex;
      flex-direction: column;
      padding: ${({ theme }) => theme.padding(1)} 0;
      gap: ${({ theme }) => theme.padding(2)};
    }
  }
`

export const TextDivider = styled.p`
  text-align: center;
  font-size: 0.8rem;
  font-weight: normal;
  margin: 0;
  color: #7d7d7d;
`

export default function MapMenu({
  onChangeMapType,
  mapType,
  onChangeMap3DOption,
  map3DOption,
  layers,
  variables,
  onChangeLayers,
  canAddLayer,
  children,
}: MapMenuProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const ref = useRef(null)
  const { context, isLoading: isLoadingAPI } = useAPI()
  const navigate = useNavigate()

  useClickAway(ref, () => {
    if (isOpen) {
      setIsOpen(prevData => !prevData)
    }
  })

  const openMenu = () => {
    setIsOpen(prevData => !prevData)
  }

  const handleAddExtraLayer = () => {
    if (!context.user) {
      return
    }
    const nextVariable = variables[0] as VariableWithResult | undefined
    if (!nextVariable) {
      return
    }
    const newLayer: Layer = recommendedLayerForVariable(
      nextVariable,
      context.user,
    )
    onChangeLayers([layers[0], newLayer])
  }
  const handleRemoveExtraLayer = () => {
    onChangeLayers([layers[0]])
  }

  return (
    <Wrapper ref={ref}>
      <NavigationMenu $isOpen={isOpen}>
        <AvatarHeader
          email={context.user?.email ?? auth.currentUser?.email}
          displayName={context.user?.displayName ?? auth.currentUser?.displayName ?? undefined}
        />

        {mapType && (
          <>
            <TextDivider>Tipo de mapa</TextDivider>
            {mapTypes.map(mapTypeOption => (
              <MenuItem
                key={mapTypeOption}
                onClick={() => onChangeMapType?.(mapTypeOption)}
                $isActive={mapType === mapTypeOption}
                label={mapTypesLabels[mapTypeOption]}
                size='xs'
              />
            ))}
            <hr />
          </>
        )}

        {map3DOption && (
          <>
            <TextDivider>Mapa 3D</TextDivider>
            {map3DOptions.map(map3DOptionOption => (
              <MenuItem
                key={map3DOptionOption}
                onClick={() => onChangeMap3DOption?.(map3DOptionOption)}
                $isActive={map3DOption === map3DOptionOption}
                size='xs'
                label={map3DOptionsLabels[map3DOptionOption]}
              />
            ))}
          </>
        )}
        {canAddLayer && (
          <>
            <hr />
            <TextDivider>Camada adicional</TextDivider>
            {layers.length === 1 ? (
              <MenuItem
                disabled={isLoadingAPI}
                onClick={handleAddExtraLayer}
                style={{ cursor: isLoadingAPI ? 'not-allowed' : 'pointer' }}
                size='xs'
                label='Adicionar camada'
              />
            ) : (
              <MenuItem
                disabled={isLoadingAPI}
                onClick={handleRemoveExtraLayer}
                size='xs'
                style={{ cursor: isLoadingAPI ? 'not-allowed' : 'pointer' }}
                label='Remover camada'
              />
            )}
          </>
        )}

        {children}

        <hr />
        <MenuItem
          onClick={() => navigate('/app/questionnaire/list', { replace: true })}
          label='Gerenciamento'
          size='xs'
        />

        <MenuItem
          label='Sair'
          size='xs'
          onClick={() =>
            signOut(auth)
              .then(() => {
                navigate('/')
              })
              .catch(error => {
                console.log('Erro ao sair', error)
              })
          }
        />
      </NavigationMenu>
      <WrapperButtons>
        <Button
          icon={isOpen ? faXmark : faBars}
          onClick={() => openMenu()}
          size='lg'
          variant='light'
        />
      </WrapperButtons>
    </Wrapper>
  )
}
